import Markdown from '@wandb/weave/common/components/Markdown';
import * as _ from 'lodash';
import React from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button, Dropdown, Segment} from 'semantic-ui-react';
import {Spec as VegaSpec} from 'vega';

import {useVegaPanelQuery, View} from '../../state/graphql/vegaPanelQuery';
import {useLoadHistoryFileTables, useRunsData} from '../../state/runs/hooks';
import {PanelProps} from '../../util/panelProps';
import * as VegaLib from '../../util/vega';
import {InstrumentedLoader as Loader} from '../utility/InstrumentedLoader';
import * as BuiltinPanelDefs from '../Vega/builtinPanelDefs';
import VegaPanelEditor from '../Vega/VegaPanelEditor';
import VegaPanelUserConfig from '../Vega/VegaPanelUserConfig';
import VegaViz from '../Vega/VegaViz';
import {
  PanelChartModal,
  PanelChartPreview,
  PanelChartSettings,
} from './../PanelCommon';
import {VegaPanelConfig} from './common';

export type VegaPanelProps = PanelProps<VegaPanelConfig>;

export type VegaPanelWithQueryProps = Omit<VegaPanelWithDataProps, 'data'> &
  ReturnType<typeof useRunsData>;

export interface VegaPanelState {
  config: string;
  editMode: boolean;
}

export function getVegaPanelDef(
  config: VegaPanelConfig,
  views: View[]
): VegaLib.VegaPanelDef | null {
  const panelDefID = getPanelDefID(config);
  if (_.startsWith(panelDefID, 'lib:')) {
    const id = panelDefID.split(':')[1];
    const view = _.find(views, v => v.id === id);
    if (view == null) {
      return null;
    }
    return {
      id,
      name: view.name,
      description: view.description,
      spec: view.spec,
    };
  } else if (panelDefID === 'custom') {
    return config.customPanelDef!;
  } else {
    return BuiltinPanelDefs.getPanelDef(panelDefID);
  }
}

export function getSpec(
  config: VegaPanelConfig,
  views: View[]
): VegaSpec | null {
  const panelDef = getVegaPanelDef(config, views);
  if (panelDef == null) {
    return null;
  }
  return VegaLib.parseSpecJSON(panelDef.spec);
}

export function getPanelDefID(config: VegaPanelConfig) {
  return config.panelDefId || BuiltinPanelDefs.IDS[0];
}

export type VegaPanelWithDataProps = VegaPanelProps & {
  views: View[];
  refetch(): void;
};

export class VegaPanel extends React.Component<
  VegaPanelWithQueryProps,
  VegaPanelState
> {
  state: VegaPanelState = {
    config: '',
    editMode: false,
  };

  ref: any;

  setRef = (ref: any) => {
    this.ref = ref;
  };

  renderUserConfig() {
    const keyInfo = this.props.keyInfo!;
    const {views, config} = this.props;
    const fieldSettings = config.fieldSettings || {};
    const runFieldSettings = config.runFieldSettings || {};
    const runFieldListSettings = config.runFieldListSettings || {};
    const historyFieldSettings = config.historyFieldSettings || {};
    const spec = getSpec(this.props.config, views);
    if (spec == null) {
      return <Segment>Vega spec invalid.</Segment>;
    }
    return (
      <VegaPanelUserConfig
        refs={VegaLib.parseSpec(spec) || []}
        runFieldListSettings={runFieldListSettings}
        runFieldOptions={VegaLib.runFieldOptions(this.props.data.keyInfo)}
        historyFieldOptions={VegaLib.historyFieldOptions(keyInfo)}
        fieldSettings={fieldSettings}
        runFieldSettings={runFieldSettings}
        historyFieldSettings={historyFieldSettings}
        updateUserSettings={newSettings =>
          this.props.updateConfig(_.merge({}, config, newSettings))
        }
      />
    );
  }

  renderEditor(views: View[]) {
    const {refetch} = this.props;
    const keyInfo = this.props.keyInfo!;
    const panelDef = getVegaPanelDef(this.props.config, views);
    if (panelDef == null) {
      return <Segment>Invalid panel.</Segment>;
    }
    return (
      <VegaPanelEditor
        pageQuery={this.props.pageQuery}
        keyInfo={keyInfo}
        state={{
          viewId: panelDef.id,
          name: panelDef.name,
          description: panelDef.description,
          spec: panelDef.spec,
          fieldSettings: this.props.config.fieldSettings || {},
          runFieldSettings: this.props.config.runFieldSettings || {},
          runFieldListSettings: this.props.config.runFieldListSettings || {},
          historyFieldSettings: this.props.config.historyFieldSettings || {},
        }}
        onCancel={() => this.setState({editMode: false})}
        onApplyCustom={newSpec => {
          this.props.updateConfig({
            panelDefId: 'custom',
            customPanelDef: {
              name: 'Custom',
              description: '',
              spec: newSpec,
            },
          });
          this.setState({editMode: false});
        }}
        onApplyFromLibrary={id => {
          this.props.updateConfig({
            panelDefId: 'lib:' + id,
            customPanelDef: undefined,
          });
          this.setState({editMode: false});
        }}
        vegaViews={views}
        refetchVegaViews={refetch}
      />
    );
  }

  renderConfig() {
    const {config, views} = this.props;
    let options = views.map(vegaView => {
      const optID = 'lib:' + vegaView.id;
      return {
        key: optID,
        value: optID,
        text: vegaView.name,
      };
    });
    options = options.concat(
      BuiltinPanelDefs.IDS.map(id => ({
        key: id,
        value: id,
        text: '[W&B builtin] ' + BuiltinPanelDefs.getPanelDef(id)!.name,
      }))
    );
    if (config.panelDefId === 'custom') {
      options.push({key: 'custom', value: 'custom', text: 'custom'});
    }
    const curPanelDef = getVegaPanelDef(config, views);
    return (
      <PanelChartModal>
        <PanelChartPreview>{this.renderViz()}</PanelChartPreview>
        <PanelChartSettings>
          <h5>Type</h5>
          <Dropdown
            search
            selection
            options={options}
            value={getPanelDefID(config)}
            onChange={(e, {value}) => {
              if (typeof value === 'string') {
                this.props.updateConfig({...config, panelDefId: value});
              }
            }}
          />
          <Button
            style={{marginLeft: 12}}
            onClick={() => this.setState({editMode: true})}>
            Edit
          </Button>
          {curPanelDef != null && (
            <div style={{marginTop: 16}}>
              <Markdown content={curPanelDef.description} condensed={false} />
            </div>
          )}
          {this.renderUserConfig()}
        </PanelChartSettings>
      </PanelChartModal>
    );
  }

  renderViz = () => {
    const {config, views} = this.props;
    const {
      fieldSettings,
      runFieldSettings,
      runFieldListSettings,
      historyFieldSettings,
    } = config;

    const key = historyFieldSettings?.key;

    const spec = getSpec(config, views);
    if (spec == null) {
      return <Segment>Invalid panel.</Segment>;
    }
    return (
      <>
        {key && (
          <h6 className="panel-title" title={key}>
            {key}
          </h6>
        )}
        <VegaViz
          spec={spec}
          data={this.props.data}
          userSettings={{
            fieldSettings: fieldSettings || {},
            runFieldSettings: runFieldSettings || {},
            runFieldListSettings: runFieldListSettings || {},
            historyFieldSettings: historyFieldSettings || {},
          }}
        />
      </>
    );
  };

  render() {
    const {views, data} = this.props;
    if (data.loading) {
      return <Loader active name="vega-panel-render-viz-loader" />;
    }
    if (this.props.configMode) {
      if (this.state.editMode) {
        return this.renderEditor(views);
      } else {
        return this.renderConfig();
      }
    } else {
      return this.renderViz();
    }
  }
}

const VegaPanelWithDataViews = (props: VegaPanelWithDataProps) => {
  const {pageQuery, config, views} = props;
  const panelDef = getVegaPanelDef(config, views);
  const query = VegaLib.runsDataQuery(
    pageQuery,
    {
      fieldSettings: config.fieldSettings || {},
      runFieldSettings: config.runFieldSettings || {},
      runFieldListSettings: config.runFieldListSettings || {},
      historyFieldSettings: config.historyFieldSettings || {},
    },
    panelDef != null ? panelDef.spec : undefined
  );
  let runsQuery = useRunsData(query);
  runsQuery = useLoadHistoryFileTables(query, runsQuery);
  return <VegaPanel {...props} {...runsQuery} />;
};

const VegaPanelContainer = (props: VegaPanelProps) => {
  const {entityName, projectName} = props.query;
  const {panelDefId} = props.config;
  const vegaPanelQuery = useVegaPanelQuery({
    entityName,
    projectName,
    viewId: panelDefId,
  });
  if (vegaPanelQuery.loading) {
    return <Loader name="vega-panel-container-loader" />;
  }
  return (
    <VegaPanelWithDataViews
      {...props}
      views={vegaPanelQuery.views}
      refetch={vegaPanelQuery.refetch}
    />
  );
};

export default VegaPanelContainer;
